const BASE64_MARKER = ';base64,';

/**
 * Converts a blob to a data url.
 * @param blob The blob to convert.
 * @returns The data url.
 */
export const blobToDataURL = async (blob: Blob, type?: string): Promise<string> => {
    let resultDataUrl = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result as string);
        reader.onerror = reject;
        reader.readAsDataURL(blob);
    }) as string;

    //Adjust data url type if doesn't match what we wanted
    if(type && blob.type != type) {
        let base64 = dataURLToBase64(resultDataUrl);
        resultDataUrl = base64ToDataUrl(type, base64);
    }

    return resultDataUrl;
};

/**
 * Converts a data url to a base64 string.
 * @param dataUrl The data url to convert.
 * @returns The base64 string.
 */
export const dataURLToBase64 = (dataUrl: string): string => {
    const parts = dataUrl.split(BASE64_MARKER);
    return parts[1];
};

/**
 * Converts a data url to a blob.
 * @param dataUrl The data url to convert.
 * @returns The blob.
 */
export const dataURLToBlob = (dataUrl: string): Blob => {
    // 1. Handle non-Base64 encoded Data URLs (e.g., plain text or URL-encoded values)
    if (!dataUrl.includes(BASE64_MARKER)) {
        const parts = dataUrl.split(',');
        const contentType = parts[0].split(':')[1];
        const raw = decodeURIComponent(parts[1]);

        return new Blob([raw], { type: contentType });
    }

    // 2. Extract content type and the base64-encoded data payload
    const parts = dataUrl.split(BASE64_MARKER);
    const contentType = parts[0].split(':')[1];

    // 3. Decode the base64 string into a binary string
    // Note: window.atob requires a browser environment or a suitable polyfill
    const raw = window.atob(parts[1]);
    const rawLength = raw.length;

    // 4. Convert the binary string into a Uint8Array for safe Blob creation
    const uInt8Array = new Uint8Array(rawLength);

    for (let i = 0; i < rawLength; ++i) {
        uInt8Array[i] = raw.charCodeAt(i);
    }

    return new Blob([uInt8Array], { type: contentType });
};

/**
 * Converts a base64 string to a data url.
 * @param type The content type of the data url.
 * @param base64 The base64 string to convert.
 * @returns The data url.
 */
export const base64ToDataUrl = (type: string, base64: string): string => {
    return `data:${type};base64,${base64}`;
};

/**
 * Starts a download of a file.
 * @param fileData The file data to download.
 * @param fileName The name of the file.
 * @param mimeType The mime type of the file.
 */
export const downloadFile = (fileData: Blob | string, fileName: string, mimeType: string = 'text/plain') => {
    const windowURL = window.URL || window.webkitURL;

    // Create a temporary, hidden anchor element and append it to the DOM
    const a = document.createElement('a');
    a.setAttribute('style', 'display: none');
    document.body.appendChild(a);

    // Create a Blob from the raw data and generate a unique temporary Object URL
    const file = new Blob([fileData], { type: mimeType });
    const fileURL = windowURL.createObjectURL(file);

    // Configure the anchor to point to the temporary URL and specify the target file name
    a.href = fileURL;
    a.download = fileName;

    // Programmatically trigger the browser's download prompt
    a.click();

    // Cleanup: revoke the Object URL to prevent memory leaks and remove the element from the DOM
    windowURL.revokeObjectURL(fileURL);
    a.remove();
};